/**
 * urlPolicy entity class
 */

import { keyValue } from '../../../app/helpers/entity'
import { streamPolicyOperators, streamPolicyPropertiesNames } from '../../streams/urlRule.directive'

/**
 * UrlPolicyFactory
 * returns urlPolicy entity class
 *
 * @class UrlPolicyFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.urlPolicy} class
 *
 * @ngdoc factory
 * @name mwApp.factory:UrlPolicyFactory
 */
/* @ngInject */
export default function UrlPolicyFactory ($http, Entity, DefaultStream, onOffMixin) {
  /* === UrlPolicy class === */

  /**
   * UrlPolicy model
   *
   * @class UrlPolicy
   * @classdesc UrlPolicy model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @return {models.UrlPolicy} UrlPolicy entity
   */
  function UrlPolicy () {
    const obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }

  UrlPolicy.prototype = Object.create(Entity.prototype)
  UrlPolicy.prototype.constructor = UrlPolicy

  /* === UrlPolicy instance methods & fields === */

  Object.assign(UrlPolicy.prototype, {

    /**
     * instance fields
     *
     * @memberof models.UrlPolicy
     * @type {Object}
     */
    _fields: {
      defaultStreamPolicy: Boolean,
      enabled: Boolean,
      id: String,
      lastUpdated: String,
      listedChannels: Number,
      listedLiveEvents: Number,
      listedTvShows: Number,
      listedVods: Number,
      name: String,
      rule: {
        id: String,
        name: String,
        description: String,
        enabled: Boolean,
        operator: String,
        where: [
          {
            property: String,
            operator: String,
            terms: [String]
          }
        ],
        otherwise: null,
        then: null
      },
      updatedBy: String,
      $rules: [],
      $isAssignedPolicy: Boolean
    },
    /**
     * url template
     *
     * @memberof models.UrlPolicy#
     * @type {String}
     */
    _urlTemplate: 'api/stream-policies/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.UrlPolicy#
     * @type {String}
     */
    _publicUrlTemplate: 'streams/url-policies/{{::id}}',

    /**
     * Prepare UrlPolicy
     *
     * @memberof models.UrlPolicy#
     * @override
     */
    _prepare: function () {
      this.$isAssignedPolicy = !!(this.listedChannels || this.listedLiveEvents || this.listedTvShows || this.listedVods)

      if (this.id && this.rule) {
        UrlPolicy.handleRules(this.rule, this.$rules)
      } else {
        // preselected first rule
        this.$rules = [{
          name: '',
          description: '',
          enabled: true,
          where: [{ property: 'platform', operator: 'ONE_OF', terms: [] }],
          operator: 'OR',
          $subRules: []
        }]
        this.rule = {}
        this.enabled = true
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.UrlPolicy#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      return Entity.prototype._object.apply(this, arguments)
    }
  }, onOffMixin)

  /* === UrlPolicy static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(UrlPolicy, Entity)
  UrlPolicy._name = 'UrlPolicy'
  UrlPolicy._publicName = 'Streaming URL Policy'
  UrlPolicy.init()

  UrlPolicy.handleRules = function (originalPolicyRules, parsedPolicyRules) {
    const { enabled, id, name, description, operator, where } = originalPolicyRules
    const rule = { enabled, id, name, description, operator, where, $subRules: [] }

    // generating rule name by front
    rule.name = (streamPolicyPropertiesNames[where[0].property]?.name || where[0].property) + ' ' + (streamPolicyOperators[where[0].operator] || where[0].operator) + ' ' +
    where[0].terms.map((term) => streamPolicyPropertiesNames[where[0].property]?.items?.[term]?.name || term).join(', ')
    parsedPolicyRules.push(rule)
    for (const type of ['then', 'otherwise']) {
      if (originalPolicyRules[type]) {
        UrlPolicy.handleRules(originalPolicyRules[type], type === 'then' ? rule.$subRules : parsedPolicyRules)
      }
    }
  }

  UrlPolicy.prepareToLoadContentItem = function (entity) {
    if (!entity.defaultStream) { entity.defaultStream = new DefaultStream() }

    if (entity.streamPolicy && entity.policedStreams) {
      for (const rule of entity.streamPolicy.$rules) {
        if (rule.$subRules.length) {
          for (const subRule of rule.$subRules) {
            subRule.then = entity.policedStreams[subRule.id]?.then || {}
            if (entity.policedStreams[subRule.id]?.otherwise) {
              rule.then = entity.policedStreams[subRule.id].otherwise
            }
          }
        } else {
          rule.then = entity.policedStreams[rule.id]?.then || {}
        }
      }
    }
  }

  UrlPolicy.prepareToSaveContentItem = function (entity) {
    const getCondition = (conditionRule) => UrlPolicy.getCondition(conditionRule, entity)

    if (!entity.urls || (!entity.defaultStream.mediaUrl.mediaUrl && !entity.defaultStream.mediaUrl.trailerUrl)) {
      entity.urls = {}
    }

    if (entity.defaultStream.mediaUrl.mediaUrl || entity.defaultStream.mediaUrl.trailerUrl) {
      entity.urls.STB = entity.defaultStream.mediaUrl
    }

    entity.policedStreams = {}

    if (entity?.streamPolicy) {
      for (const rule of entity.streamPolicy.$rules) {
        const subRulesLength = rule.$subRules.length
        if (subRulesLength) {
          for (let i = 0; i < subRulesLength; i++) {
            const subRule = rule.$subRules[i]
            if (((i + 1) === subRulesLength && getCondition(rule)) || getCondition(subRule)) {
              entity.policedStreams[subRule.id] = {}
            }

            if (getCondition(subRule)) {
              entity.policedStreams[subRule.id].then = subRule.then
            }
            if ((i + 1) === subRulesLength && getCondition(rule)) {
              entity.policedStreams[subRule.id].otherwise = rule.then
            }
          }
        } else {
          if (getCondition(rule)) {
            entity.policedStreams[rule.id] = { then: rule.then }
          }
        }
      }
    }
  }

  UrlPolicy.getCondition = function (conditionRule, entity) {
    const then = conditionRule?.then
    if (!then) return false
    if (['show'].includes(entity.$itemType)) {
      return then.trailerUrl || then.mediaUrl?.trailerUrl || false
    }
    const streamUrl = then.mediaUrl?.mediaUrl
    return then.playbackUrl || (streamUrl != null && String(streamUrl).trim() !== '') || false
  }

  UrlPolicy.getPolicies = function (policies, byKeyValue) {
    let isLoading = false
    const number = 25
    const tableState = {
      pagination: { number },
      search: {},
      sort: { predicate: 'name', reverse: false }
    }

    return function (event = undefined, search) {
      const isScrolled = !(event && event.currentTarget) || (event.currentTarget.scrollTop >= (event.currentTarget.scrollHeight - event.currentTarget.clientHeight) * 0.6)
      const newSearch = search && (search.q !== tableState.search.predicateObject?.q)
      if (isLoading || !isScrolled || (tableState.pagination.last && !newSearch)) {
        return
      } else {
        isLoading = true
      }

      if (newSearch) {
        tableState.pagination.start = 0
        policies.length = 0
        tableState.search = { predicateObject: search }
      }

      return UrlPolicy.loadSmart(tableState)
        .then(data => {
          if (byKeyValue) {
            policies.push(...data.content.map(keyValue()))
          } else {
            policies.push(...data.content)
          }

          if (!data.last) {
            tableState.pagination.start += number
          }
          isLoading = false
        })
    }
  }

  // permissions
  UrlPolicy.access = {
    view: 'viewUrlPolicy',
    create: 'createUrlPolicy',
    edit: 'editUrlPolicy',
    delete: 'deleteUrlPolicy'
  }

  return UrlPolicy
}
